/* eslint-disable @typescript-eslint/naming-convention */
import { takeLatest, call, delay, put } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { omit } from 'lodash';
import { rcpsApi } from '../../../services/api/resources/rcps/rcps.resource';
import * as aaSubscriptionCalls from './aaSubscription.calls';
import * as planActions from '../../subscriptions/plans/plans.actions';
import { planCallsInterval } from '../../../utils/constants/api/intervals';
import {
  postProPlanSuccess,
  postProPlanFailure,
  postProSubscriptionFailure,
  postProSubscriptionSuccess,
  calculatePriceFailure,
  calculatePriceSuccess,
  createAASubscriptionFailure,
  createAASubscriptionSuccess,
  postCreateOnlyAASubscriptionSuccess
} from './pro.actions';
import { rcpErrorStatuses } from './pro.constants';

import {
  PostProPlanRequestAction,
  PostProSubscriptionRequestAction,
  CalculatePriceRequestAction,
  AaChangeRequestPayload,
  ExecuteAaSubsCallParams,
  CreateAASubscriptionRequestAction,
  ExecuteAASubscriptionRequestAction
} from './pro.types';
import history from '../../../hashHistory';
import { buildCalculatePricePostBody, findMinimalPricingForMultiRegions } from './pro.utils';
import { buildPath, routes } from '../../../utils/constants/routes';
import { addNewSubscription } from '../../subscriptions/subscriptions.actions';
import { isCloudAccountExternal } from '../../../screens/CreateSubscription/SubscriptionTypes/Pro/utils/isCloudAccountExternal';
import createApiErrorResponse from '../../../utils/createApiErrorResponse';
import { extractErrorAndShowToast } from '../../../utils/helpers/extractErrorAndShowToast';
import findRegionPriceItem from '../../../utils/cloudPricings/findRegionPriceItem';
import getCalculatorTotalPrice from '../../../utils/cloudPricings/getCalculatorTotalPrice';
import { subscriptionsApi } from '../../../services/api/resources/subscriptions/subscriptions.resource';
import { extractErrorCode } from '../../../utils/helpers/extractError';
import i18n from '../../../locale/i18n';
import { ErrorsMapCodes } from '../../../utils/constants/api/errorsMap';
import { insertMaintenanceWindow } from './pro.maintenanceWindow.calls';
import { shardTypePricingsApi } from '../../../services/api/resources/shardTypePricings/shardTypePricings.resource';

function* postProPlanSaga(action: PostProPlanRequestAction) {
  const {
    payload: { subsRcp: rcp, openSupportPageAction }
  } = action;

  try {
    const { data: rcpId }: AxiosResponse<number> = yield call(rcpsApi.create, rcp);

    yield delay(planCallsInterval);
    while (true) {
      const { data: proPlan }: AxiosResponse<RcpPlanStatusResponse> = yield call(
        rcpsApi.planStatus,
        rcpId
      );

      if (
        proPlan.status === 'plan_done' ||
        (proPlan.status === 'encryption_key_pending' && proPlan.databases.length > 0)
      ) {
        yield put(postProPlanSuccess({ rcpId, proPlan }));
        break;
      }

      if (rcpErrorStatuses.some((status) => status === proPlan.status)) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw createApiErrorResponse(proPlan.error_code);
      }

      yield delay(planCallsInterval);
    }
  } catch (e) {
    yield put(postProPlanFailure());
    const errorCode = extractErrorCode(e.response.data);
    const PLAN_ERRORS_WITH_ACTION: ErrorsMapCodes[] = [
      'rcp-cannot-place-shard-new',
      'rcp-no-valid-cluster-options-new'
    ];
    const primaryAction = PLAN_ERRORS_WITH_ACTION.includes(errorCode)
      ? {
          label: i18n.t('subscriptionDetails.contactSupport'),
          onClick: openSupportPageAction
        }
      : undefined;

    extractErrorAndShowToast(e, primaryAction);
  }
}

function* postProSubscription(action: PostProSubscriptionRequestAction) {
  const { onSuccess, onError } = action.payload;

  try {
    const {
      data: { subscription }
    }: AxiosResponse<{ subscription: Subscription }> = yield call(subscriptionsApi.create, {
      subscription: omit(
        action.payload,
        'maintenance_window',
        'idempotency_key',
        'onSuccess',
        'onError'
      ),
      headers: { 'Idempotency-Key': action.payload.idempotency_key }
    });

    if (action.payload.maintenance_window) {
      yield insertMaintenanceWindow(subscription.id, action.payload.maintenance_window);
    }

    yield put(postProSubscriptionSuccess());
    yield put(planActions.getPlans());
    yield put(addNewSubscription(subscription));
    history.push(
      buildPath(routes.subscriptions.subscription.db.root, { subscriptionId: subscription.id })
    );
    onSuccess && onSuccess();
  } catch (e) {
    yield put(postProSubscriptionFailure());
    extractErrorAndShowToast(e);
    const message = e.response?.message || e.message;
    onError && onError(message);
  }
}

function* calculatePriceSaga({ payload }: CalculatePriceRequestAction) {
  const { databases_to_create, cloud_account } = payload;

  try {
    if (databases_to_create.length) {
      const calculatePricePostBody = buildCalculatePricePostBody(payload);
      const {
        data: { shardTypePricings }
      }: AxiosResponse<ShardTypePricingListResponse> = yield call(
        shardTypePricingsApi.calculatePrice,
        calculatePricePostBody
      );
      const price = getCalculatorTotalPrice({
        ...payload,
        databases: payload.databases_to_create,
        shardTypePricings,
        isExternal: isCloudAccountExternal(payload.cloud_account)
      });

      yield put(calculatePriceSuccess({ price, shardTypePricingList: shardTypePricings }));
    } else {
      let minimalCalculatedPrice = 0;
      let comparedPrice = 0;
      let comparedSize = 0;

      if (!isCloudAccountExternal(cloud_account)) {
        if (payload.isAA) {
          minimalCalculatedPrice = findMinimalPricingForMultiRegions(payload);
        } else {
          const regionPriceItem = findRegionPriceItem({ ...payload, regionName: payload.region });

          if (regionPriceItem) {
            minimalCalculatedPrice = regionPriceItem.price;

            if (payload.rof) {
              comparedPrice = regionPriceItem.compared_price;
              comparedSize = regionPriceItem.compared_size;
            }
          }
        }
      }

      yield put(
        calculatePriceSuccess({
          price: minimalCalculatedPrice,
          comparedPrice,
          comparedSize
        })
      );
    }
  } catch (e) {
    if (e.response) {
      yield put(calculatePriceFailure(e.response?.statusText));
      extractErrorAndShowToast(e);
    }
  }
}

function* executeAASubscription(
  params: ExecuteAaSubsCallParams,
  onSuccess?: () => void,
  onError?: (error: string) => void
) {
  try {
    const {
      data
    }: AxiosResponse<{
      aaChangeRequest: AaChangeRequestPayload;
    }> = yield call(aaSubscriptionCalls.executeAaChangeRequest, params);

    const subscriptionId = data.aaChangeRequest.subscription_id;
    const { data: subsResponse }: AxiosResponse<{ subscription: Subscription }> = yield call(
      subscriptionsApi.getOne,
      subscriptionId
    );

    yield put(planActions.getPlans());
    yield put(addNewSubscription(subsResponse.subscription));
    yield put(createAASubscriptionSuccess());

    history.push(
      buildPath(routes.subscriptions.subscription.db.root, {
        subscriptionId
      })
    );

    onSuccess && onSuccess();

    return data.aaChangeRequest;
  } catch (e) {
    if (e.response) {
      yield put(createAASubscriptionFailure(e.response?.message));
      extractErrorAndShowToast(e);
    }

    const message = e.response?.message || e.message;

    onError && onError(message);
  }

  return null;
}

function* getAAChangeRequestStatus(
  params: ExecuteAaSubsCallParams,
  isShouldExecuteRequest = true,
  onSuccess?: () => void,
  onError?: (error: string) => void
) {
  yield delay(planCallsInterval);

  try {
    while (true) {
      const {
        data: { aaChangeRequest }
      }: AxiosResponse<{
        aaChangeRequest: AaChangeRequestPayload;
      }> = yield call(aaSubscriptionCalls.getAAChangeRequestStatus, params.aaChangeRequestId);

      const responseStatus = aaChangeRequest.status;

      if (responseStatus === 'plan_done_provision_needed') {
        if (isShouldExecuteRequest) {
          return yield executeAASubscription(params, onSuccess, onError);
        }

        return yield put(
          postCreateOnlyAASubscriptionSuccess({ aaChangeRequestPayload: aaChangeRequest })
        );
      }

      if (responseStatus === 'plan_failed') {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw createApiErrorResponse('plan-failed');
      }

      yield delay(planCallsInterval);
    }
  } catch (e) {
    const message = e.response?.message || e.message;
    yield put(createAASubscriptionFailure(message));
    extractErrorAndShowToast(e);
    onError && onError(message);
  }

  return null;
}

function* createAAChangeRequest(
  { payload }: CreateAASubscriptionRequestAction,
  isShouldExecuteRequest = true
) {
  const { onSuccess, onError } = payload;

  try {
    const {
      data
    }: AxiosResponse<{
      aaChangeRequest: AaChangeRequestPayload;
    }> = yield call(aaSubscriptionCalls.aaChangeRequest, {
      aaChangeRequest: payload.aaChangeBodyPayload
    });

    const aaChangeRequestId = data.aaChangeRequest.id;
    const finalAaChangeRequest: AaChangeRequestPayload | null = yield getAAChangeRequestStatus(
      {
        aaChangeRequestId,
        subscriptionName: payload.subscriptionName,
        paymentId: payload.paymentId,
        isMarketplace: payload.isMarketplace
      },
      isShouldExecuteRequest,
      onSuccess,
      onError
    );

    if (payload.maintenanceWindow?.customWindow && finalAaChangeRequest?.subscription_id) {
      yield insertMaintenanceWindow(
        finalAaChangeRequest.subscription_id,
        payload.maintenanceWindow
      );
    }
  } catch (e) {
    if (e.response) {
      yield put(createAASubscriptionFailure(e.response?.message));
      extractErrorAndShowToast(e);
    }

    const message = e.response?.message || e.message;

    onError && onError(message);
  }
}

function* createOnlyAAChangeRequest(
  createAASubscriptionRequestAction: CreateAASubscriptionRequestAction
) {
  yield createAAChangeRequest(createAASubscriptionRequestAction, false);
}

function* executeAAChangeRequest(
  createAASubscriptionRequestAction: ExecuteAASubscriptionRequestAction
) {
  const { onSuccess, onError, ...params } = createAASubscriptionRequestAction.payload;
  yield executeAASubscription(params, onSuccess, onError);
}

export default function* proSaga() {
  yield takeLatest('POST_PRO_PLAN_REQUEST', postProPlanSaga);
  yield takeLatest('POST_PRO_SUBSCRIPTION_REQUEST', postProSubscription);
  yield takeLatest('CALCULATE_PRICE_REQUEST', calculatePriceSaga);
  yield takeLatest('CREATE_AA_SUBSCRIPTION_REQUEST', createAAChangeRequest);
  yield takeLatest('CREATE_ONLY_AA_SUBSCRIPTION_REQUEST', createOnlyAAChangeRequest);
  yield takeLatest('EXECUTE_AA_SUBSCRIPTION_REQUEST', executeAAChangeRequest);
}
